/**
 * Partner Detail State
 *
 * NGXS state for managing partner data during the detail/view/edit process.
 * This state is NOT stored in local storage.
 */

import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import {
  InitializePartnerDetail,
  SetPartnerDetailField,
  UpdatePartnerDetailFields,
  ResetPartnerDetail,
  SetPartnerDetailData,
  ClearPartnerDetail,
} from './partner-detail.actions';
import { Partner, Contact, License } from 'src/app/models/schema';

/**
 * Partner Detail State Model
 */
export interface PartnerDetailStateModel {
  /** Partner ID */
  id?: string;
  /** Partner name */
  name?: string;
  /** Partner state */
  state?: string;
  /** Partner type */
  type?: string;
  /** Student population */
  studentPopulation?: number;
  /** Schools */
  schools?: string[];
  /** Initial admin emails */
  initialAdmins?: string[];
  /** Licenses */
  licenses?: License[];
  /** Contacts */
  contacts?: Contact[];
  /** Created at timestamp */
  createdAt?: number;
  /** Created by user ID */
  createdBy?: string;
}

/**
 * Default state
 */
const defaults: PartnerDetailStateModel = {
  id: undefined,
  name: undefined,
  state: undefined,
  type: undefined,
  studentPopulation: undefined,
  schools: [],
  initialAdmins: [],
  licenses: [],
  contacts: [],
  createdAt: undefined,
  createdBy: undefined,
};

/**
 * Partner Detail State
 */
@State<PartnerDetailStateModel>({
  name: 'partnerDetail',
  defaults,
})
@Injectable()
export class PartnerDetailState {
  constructor(private store: Store) {}

  /**
   * Get the entire partner detail state
   */
  @Selector()
  static getState(state: PartnerDetailStateModel): PartnerDetailStateModel {
    return state;
  }

  /**
   * Get partner ID
   */
  @Selector()
  static getId(state: PartnerDetailStateModel): string | undefined {
    return state.id;
  }

  /**
   * Get partner name
   */
  @Selector()
  static getName(state: PartnerDetailStateModel): string | undefined {
    return state.name;
  }

  /**
   * Get partner state
   */
  @Selector()
  static getPartnerState(state: PartnerDetailStateModel): string | undefined {
    return state.state;
  }

  /**
   * Get partner type
   */
  @Selector()
  static getType(state: PartnerDetailStateModel): string | undefined {
    return state.type;
  }

  /**
   * Get student population
   */
  @Selector()
  static getStudentPopulation(state: PartnerDetailStateModel): number | undefined {
    return state.studentPopulation;
  }

  /**
   * Get schools
   */
  @Selector()
  static getSchools(state: PartnerDetailStateModel): string[] {
    return state.schools || [];
  }

  /**
   * Get licenses
   */
  @Selector()
  static getLicenses(state: PartnerDetailStateModel): License[] {
    return state.licenses || [];
  }

  /**
   * Get contacts
   */
  @Selector()
  static getContacts(state: PartnerDetailStateModel): Contact[] {
    return state.contacts || [];
  }

  /**
   * Get created at timestamp
   */
  @Selector()
  static getCreatedAt(state: PartnerDetailStateModel): number | undefined {
    return state.createdAt;
  }

  /**
   * Get created by user ID
   */
  @Selector()
  static getCreatedBy(state: PartnerDetailStateModel): string | undefined {
    return state.createdBy;
  }

  /**
   * Get partner data as Partner object
   */
  @Selector()
  static getPartnerData(state: PartnerDetailStateModel): Partial<Partner> {
    return {
      id: state.id,
      name: state.name,
      state: state.state,
      type: state.type as any,
      studentPopulation: state.studentPopulation,
      schools: state.schools,
      licenses: state.licenses,
      contacts: state.contacts,
      createdAt: state.createdAt,
      createdBy: state.createdBy,
    };
  }

  /**
   * Check if form has any data
   */
  @Selector()
  static hasData(state: PartnerDetailStateModel): boolean {
    return !!(
      state.name ||
      state.state ||
      state.type ||
      state.studentPopulation ||
      (state.schools && state.schools.length > 0) ||
      (state.licenses && state.licenses.length > 0) ||
      (state.contacts && state.contacts.length > 0)
    );
  }

  /**
   * Initialize partner detail form
   */
  @Action(InitializePartnerDetail)
  initializePartnerDetail(ctx: StateContext<PartnerDetailStateModel>) {
    ctx.setState(defaults);
  }

  /**
   * Set a partner field
   */
  @Action(SetPartnerDetailField)
  setPartnerDetailField(
    ctx: StateContext<PartnerDetailStateModel>,
    action: SetPartnerDetailField
  ) {
    ctx.patchState({
      [action.field]: action.value,
    } as Partial<PartnerDetailStateModel>);
  }

  /**
   * Update multiple partner fields
   */
  @Action(UpdatePartnerDetailFields)
  updatePartnerDetailFields(
    ctx: StateContext<PartnerDetailStateModel>,
    action: UpdatePartnerDetailFields
  ) {
    ctx.patchState(action.updates as Partial<PartnerDetailStateModel>);
  }

  /**
   * Reset partner detail form
   */
  @Action(ResetPartnerDetail)
  resetPartnerDetail(ctx: StateContext<PartnerDetailStateModel>) {
    ctx.setState(defaults);
  }

  /**
   * Set partner data (e.g., when loading existing partner)
   */
  @Action(SetPartnerDetailData)
  setPartnerDetailData(
    ctx: StateContext<PartnerDetailStateModel>,
    action: SetPartnerDetailData
  ) {
    ctx.patchState(action.partner as Partial<PartnerDetailStateModel>);
  }

  /**
   * Clear partner detail form
   */
  @Action(ClearPartnerDetail)
  clearPartnerDetail(ctx: StateContext<PartnerDetailStateModel>) {
    ctx.setState(defaults);
  }
}

